/*
 * Figma `124:159` — SA3-02 · Events and Workspaces.
 *
 * `TableTemplate`: every event on the platform as a registry, with the one you opened in a drawer.
 * Presentational — SuperAdmin already holds the list and owns every action on it, including the
 * confirmations for the destructive ones.
 *
 * The frame asks for "risk state" per row, and the honest version of that is derived rather than
 * stored: an event that is live with nothing sold, or live with no capacity configured at all, is
 * an event that will disappoint somebody, and it is the only thing on this screen worth sorting by.
 * `eventRisk` says which and why; it never invents a severity the data cannot support.
 *
 * Entering an organizer's console from here is a real act — it reaches their money and their
 * buyers' details — so the drawer says it is recorded rather than presenting it as a link.
 */
import { useState, type ReactNode } from 'react';
import { Alert, Button, Card, CredentialReveal, Drawer, FilterChips, KpiCard, SearchField, StatusBadge, TableTemplate } from '../..';
import type { BadgeTone, Column } from '../..';
import type { Destination } from '../../destinations';
import type { TenantSummaryDto } from '../../../api';

type Tab = 'all' | 'active' | 'paused' | 'archived';

const baht = (n: number) => `฿${Math.round(n).toLocaleString('th-TH')}`;

const STATUS_TONE: Record<string, BadgeTone> = { active: 'success', paused: 'warning', archived: 'neutral' };
const STATUS_LABEL: Record<string, string> = { active: 'เปิดขาย', paused: 'พักการขาย', archived: 'เก็บถาวร' };

export interface EventRisk { tone: BadgeTone; label: string; why: string }

/**
 * Derived from what the summary can prove. No severity the data does not support.
 *
 * Order is the design. One risk shows per row, so the first match is a claim that it is the most
 * useful thing to say about this event — and the tie-break, once the faults are out of the way, is
 * **which fact the rest of the row cannot already tell you**.
 *
 * `ยังไม่มียอดขาย` and `ขายหมด` are both readable straight off the "ขายแล้ว / โควตา" column two
 * cells away. How an event's slips get checked is readable nowhere else on this screen — it used to
 * need the drawer, opened one event at a time — so it takes the badge when they collide.
 *
 * That ordering also puts the verifier state in front of someone while the event is still being set
 * up, which is when it is cheapest to fix. Waiting until the first sale means the warning arrives
 * with money already moving.
 */
export function eventRisk(e: TenantSummaryDto): EventRisk | null {
  if (e.status !== 'active') return null;
  if (e.capacity === 0) {
    return { tone: 'danger', label: 'ขายไม่ได้', why: 'เปิดขายอยู่แต่ยังไม่มีโควตาบัตรเลย — ผู้ซื้อกดซื้อไม่ได้' };
  }
  // TICK-160 — buyers can pay and approval can succeed, but no ticket link can be issued. Ranked just
  // below "cannot sell at all": money moves and nothing arrives, which is the worse of the two to find late.
  if (e.ticketLinkBlock) {
    return { tone: 'danger', label: 'ส่งตั๋วไม่ได้', why: e.ticketLinkBlock.message };
  }
  if (e.slipVerifier === 'blocked') {
    return {
      tone: 'danger',
      label: 'ตรวจสลิปหยุดทำงาน',
      why: 'อีเวนต์นี้ตั้ง key ไว้ แต่ผู้ให้บริการที่ถืออยู่ถูกปิดทั้งแพลตฟอร์ม — สลิปจะไม่ถูกตรวจอัตโนมัติ ทั้งที่ตั้งค่าไว้แล้ว',
    };
  }
  /*
   * `neutral`, not a warning: checking slips by hand is a way of working, not a fault. It outranks
   * the two sales notes below because it is the only one of the three that this screen cannot tell
   * you any other way — not because it is more urgent than they are.
   */
  if (e.slipVerifier === 'none') {
    return {
      tone: 'neutral',
      label: 'ตรวจสลิปด้วยคน',
      why: 'ยังไม่มี key ของผู้ให้บริการตรวจสลิป — สลิปทุกใบของอีเวนต์นี้ต้องให้คนตรวจเอง ตั้ง key ได้ในลิ้นชักของอีเวนต์',
    };
  }
  if (e.sold === 0) {
    return { tone: 'warning', label: 'ยังไม่มียอดขาย', why: 'เปิดขายแล้วแต่ยังไม่มีใครซื้อ — ตรวจว่าลิงก์ออกไปถึงคนหรือยัง' };
  }
  if (e.sold >= e.capacity) {
    return { tone: 'accent', label: 'ขายหมด', why: 'ขายครบโควตาแล้ว — เพิ่มโควตาหรือปิดการขายได้' };
  }
  return null;
}

export function EventsScreen({
  destination, events, loading, error, onRetry,
  onOpenNew, onToggleStatus, onReset, onDelete, onSaveTemplate, statusBusy, eventConsoleHref,
  revealOwnerToken, eventLinks, vendorKeys,
}: {
  destination: Destination;
  events: readonly TenantSummaryDto[];
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  onOpenNew?: () => void;
  onToggleStatus?: (e: TenantSummaryDto) => void;
  onReset?: (e: TenantSummaryDto) => void;
  onDelete?: (e: TenantSummaryDto) => void;
  onSaveTemplate?: (e: TenantSummaryDto) => void;
  statusBusy?: string | null;
  /** Where an organizer console lives, given a slug. */
  eventConsoleHref?: (slug: string) => string;
  /**
   * The event's current owner token, fetched only when somebody asks for it.
   *
   * SuperAdmin's reason for existing on this screen is to be able to go in and set an event up when
   * its organizer cannot. That needs the organizer's own credential, and the classic events card
   * had it; the rebuilt drawer did not, which left a superadmin able to see every number about an
   * event and unable to open it. `null` back means there is no live owner token to show — issue a
   * new one — which is a different answer from a failure, and `CredentialReveal` says so.
   */
  revealOwnerToken?: (e: TenantSummaryDto) => Promise<string | null>;
  /** Absolute links to the event's public and staff entry points, for handing to an organizer. */
  eventLinks?: (e: TenantSummaryDto) => readonly { label: string; href: string }[];
  /**
   * The event's slip-verification keys, as a node rather than a set of callbacks.
   *
   * They are per-event secrets with their own fetch, their own reveal and their own confirmation,
   * and building that here would make this screen hold a second event's worth of state it has no
   * other use for. The page supplies a card bound to one event; this screen decides only where in
   * the drawer it sits and never sees a key.
   */
  vendorKeys?: (e: TenantSummaryDto) => ReactNode;
}) {
  const [tab, setTab] = useState<Tab>('all');
  const [query, setQuery] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);

  const open = openId ? events.find((e) => e.id === openId) ?? null : null;

  const rows = events.filter((e) => {
    if (tab !== 'all' && e.status !== tab) return false;
    if (!query.trim()) return true;
    const needle = query.trim().toLowerCase();
    return `${e.name} ${e.slug} ${e.ownerEmail}`.toLowerCase().includes(needle);
  });

  const revenue = events.reduce((sum, e) => sum + e.revenue, 0);
  /*
   * Counts the `danger` risks, which is now two different faults rather than one. The label has to
   * cover both or it lies about its own number: an event whose verifier is switched off sells
   * perfectly well, it just leaves every buyer waiting on a person.
   */
  const atRisk = events.filter((e) => eventRisk(e)?.tone === 'danger').length;

  const columns: Column<TenantSummaryDto>[] = [
    {
      key: 'name',
      header: 'อีเวนต์',
      cell: (e) => (
        <span className="jt-cell-stack">
          <span>{e.name || e.slug}</span>
          <span className="jt-cell-sub jt-mono">{e.slug} · {e.ownerEmail}</span>
        </span>
      ),
    },
    {
      key: 'status',
      header: 'สถานะ',
      width: '210px',
      cell: (e) => {
        const risk = eventRisk(e);
        return (
          <span className="jt-cell-stack">
            <span className="jt-inline-actions">
              <StatusBadge tone={STATUS_TONE[e.status] ?? 'neutral'}>{STATUS_LABEL[e.status] ?? e.status}</StatusBadge>
              {risk && <StatusBadge tone={risk.tone}>{risk.label}</StatusBadge>}
            </span>
            {risk && <span className="jt-cell-sub">{risk.why}</span>}
          </span>
        );
      },
    },
    { key: 'sold', header: 'ขายแล้ว / โควตา', align: 'end', width: '210px', cell: (e) => <span className="jt-cell-stack"><span className="jt-mono">{e.sold.toLocaleString('th-TH')} / {e.capacity.toLocaleString('th-TH')}</span>{((e.reservedOnline ?? 0) > 0 || (e.reservedPrinted ?? 0) > 0) && <span className="jt-cell-sub">รอชำระ {e.reservedOnline ?? 0} · พิมพ์รอขาย {e.reservedPrinted ?? 0}</span>}</span> },
    { key: 'orders', header: 'คำสั่งซื้อ', align: 'end', width: '120px', cell: (e) => <span className="jt-mono">{e.orders.toLocaleString('th-TH')}</span> },
    { key: 'revenue', header: 'รายได้', align: 'end', width: '140px', cell: (e) => <span className="jt-mono">{baht(e.revenue)}</span> },
    { key: 'plan', header: 'แพ็กเกจ', width: '110px', cell: (e) => <StatusBadge tone={e.plan === 'pro' ? 'accent' : 'neutral'}>{e.plan}</StatusBadge> },
  ];

  return (
    <TableTemplate
      gate={{ destination, loading, error: error ?? null, onRetry }}
      kpis={[
        <KpiCard key="all" label="อีเวนต์ทั้งหมด" value={events.length.toLocaleString('th-TH')} />,
        <KpiCard key="live" label="เปิดขายอยู่" value={events.filter((e) => e.status === 'active').length.toLocaleString('th-TH')} />,
        <KpiCard key="risk" label="เปิดขายแต่ติดปัญหา" value={atRisk.toLocaleString('th-TH')} tone={atRisk > 0 ? 'danger' : undefined} />,
        <KpiCard key="rev" label="รายได้รวม" value={baht(revenue)} />,
      ]}
      toolbar={
        <>
          <FilterChips
            chips={[
              { key: 'all', label: 'ทั้งหมด', count: events.length },
              { key: 'active', label: 'เปิดขาย', count: events.filter((e) => e.status === 'active').length },
              { key: 'paused', label: 'พัก', count: events.filter((e) => e.status === 'paused').length },
              { key: 'archived', label: 'เก็บถาวร', count: events.filter((e) => e.status === 'archived').length },
            ]}
            active={tab}
            onSelect={(k) => setTab(k as Tab)}
            label="สถานะอีเวนต์"
          />
          <SearchField value={query} onChange={setQuery} label="ค้นหาอีเวนต์" placeholder="ชื่อ / slug / อีเมลเจ้าของ" />
          {onOpenNew && <Button size="sm" variant="primary" onClick={onOpenNew}>＋ สร้างอีเวนต์</Button>}
        </>
      }
      table={{
        label: 'อีเวนต์บนแพลตฟอร์ม',
        columns,
        rows,
        rowKey: (e) => e.id,
        filtered: tab !== 'all' || query.trim().length > 0,
        onClearFilters: () => { setTab('all'); setQuery(''); },
        emptyTitle: 'ยังไม่มีอีเวนต์',
        onRowClick: (e) => setOpenId(e.id),
        activeRowKey: openId,
        card: (e) => (
          <span className="jt-cell-stack">
            <span>{e.name || e.slug}</span>
            <span className="jt-cell-sub jt-mono">{e.slug} · {baht(e.revenue)}</span>
            <StatusBadge tone={STATUS_TONE[e.status] ?? 'neutral'}>{STATUS_LABEL[e.status] ?? e.status}</StatusBadge>
          </span>
        ),
      }}
      drawer={
        <Drawer
          open={!!open}
          onClose={() => setOpenId(null)}
          title={open ? (open.name || open.slug) : ''}
          footer={open && (
            <>
              {onSaveTemplate && (
                <Button variant="ghost" onClick={() => onSaveTemplate(open)}>บันทึกเป็นเทมเพลต</Button>
              )}
              {onToggleStatus && (
                <Button
                  variant="secondary"
                  disabled={statusBusy === open.id}
                  onClick={() => onToggleStatus(open)}
                >
                  {open.status === 'active' ? 'พักการขาย' : 'เปิดขาย'}
                </Button>
              )}
            </>
          )}
        >
          {open && (
            <>
              {eventRisk(open) && (
                <Alert
                  tone={eventRisk(open)!.tone === 'danger' ? 'danger' : 'warning'}
                  title={eventRisk(open)!.label}
                >
                  {eventRisk(open)!.why}
                </Alert>
              )}

              <Card title="สรุป">
                <ul className="jt-order-lines">
                  <li><span>เจ้าของ</span><span className="jt-mono">{open.ownerEmail}</span></li>
                  <li><span>วันงาน</span><span>{open.dateLabel || '—'}</span></li>
                  <li><span>ทีมงาน</span><span className="jt-mono">{open.teamCount} คน</span></li>
                  <li><span>ขายแล้ว</span><span className="jt-mono">{open.sold} / {open.capacity}</span></li>
                  <li><span>ออเดอร์รอชำระ</span><span className="jt-mono">{(open.reservedOnline ?? 0).toLocaleString('th-TH')}</span></li>
                  <li><span>บัตรพิมพ์รอขาย</span><span className="jt-mono">{(open.reservedPrinted ?? 0).toLocaleString('th-TH')}</span></li>
                  <li className="jt-order-total"><span>รายได้</span><span className="jt-mono">{baht(open.revenue)}</span></li>
                </ul>
              </Card>

              {revealOwnerToken && (
                <Card
                  title="Token ของผู้จัด"
                  sub="ใช้เข้าคอนโซลของอีเวนต์นี้แทนเจ้าของ เมื่อเขาเข้าเองไม่ได้"
                >
                  <CredentialReveal
                    label="Owner Token"
                    masked="••••••••••••••••"
                    reveal={() => revealOwnerToken(open)}
                  />
                </Card>
              )}

              {eventLinks && eventLinks(open).length > 0 && (
                <Card title="ลิงก์ของอีเวนต์" sub="ส่งให้ผู้จัดหรือทีมหน้างานได้ตรง ๆ">
                  <ul className="jt-order-lines">
                    {eventLinks(open).map((l) => (
                      <li key={l.href}>
                        <span>{l.label}</span>
                        <a className="jt-mono" href={l.href} target="_blank" rel="noreferrer">{l.href}</a>
                      </li>
                    ))}
                  </ul>
                </Card>
              )}

              {vendorKeys && vendorKeys(open)}

              {eventConsoleHref && (
                <Card title="เข้าคอนโซลของผู้จัด" sub="เปิดหน้าจัดการของอีเวนต์นี้ในแท็บใหม่">
                  <Alert tone="warning" title="การเข้าใช้นี้ถูกบันทึกไว้">
                    คอนโซลของผู้จัดเข้าถึงเงิน ข้อมูลผู้ซื้อ และการตั้งค่าการส่งมอบของอีเวนต์นี้ — เข้าเมื่อจำเป็นเท่านั้น
                  </Alert>
                  <div className="jt-inline-actions">
                    <a
                      className="jt-btn" data-style="secondary" data-size="sm" data-emphasis="normal"
                      href={eventConsoleHref(open.slug)} target="_blank" rel="noreferrer"
                    >
                      เปิดคอนโซล {open.slug}
                    </a>
                  </div>
                </Card>
              )}

              {(onReset || onDelete) && (
                <Card title="เขตอันตราย" danger>
                  {onReset && (
                    <div className="jt-inline-actions">
                      <Button variant="destructive" size="sm" onClick={() => onReset(open)}>ล้างข้อมูลการขาย</Button>
                      <span className="jt-field-hint">ลบออร์เดอร์และยอดขายทั้งหมดของอีเวนต์นี้</span>
                    </div>
                  )}
                  {onDelete && !open.isDefault && (
                    <div className="jt-inline-actions">
                      <Button variant="destructive" size="sm" onClick={() => onDelete(open)}>ลบอีเวนต์</Button>
                      <span className="jt-field-hint">ลบทั้งอีเวนต์ออกจากแพลตฟอร์ม</span>
                    </div>
                  )}
                  {open.isDefault && (
                    <p className="jt-field-hint">อีเวนต์เริ่มต้นของแพลตฟอร์มลบไม่ได้</p>
                  )}
                </Card>
              )}
            </>
          )}
        </Drawer>
      }
    />
  );
}
